/**
 * Auth storage helpers.
 * Client portal and DigiX staff sessions use sessionStorage (cleared when the browser
 * tab/window closes), unless "Remember me" was ticked at sign-in: then localStorage,
 * so the session survives restarts until sign-out or the refresh token expires.
 */

const REMEMBER_KEY = "remember_me";
const REMEMBERED_EMAIL_KEY = "remembered_email";

const AUTH_KEYS = [
  "access_token",
  "refresh_token",
  "user",
  "client",
  "account_type",
  "scope",
  "tenant",
  "tenants",
  "is_authenticated",
  "user_role_permissions",
  "portal_notice",
];

function safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch (_) {
    return null;
  }
}

function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch (_) {}
}

function safeRemove(storage, key) {
  try {
    storage.removeItem(key);
  } catch (_) {}
}

function clearKeys(storage) {
  AUTH_KEYS.forEach((key) => safeRemove(storage, key));
}

export function isRememberMe() {
  return safeGet(localStorage, REMEMBER_KEY) === "true";
}

export function getRememberedEmail() {
  return safeGet(localStorage, REMEMBERED_EMAIL_KEY) || "";
}

/** Call before persisting a sign-in: picks the storage for the session and remembers the email. */
export function setRememberMe(remember, email) {
  if (remember) {
    safeSet(localStorage, REMEMBER_KEY, "true");
    if (email) safeSet(localStorage, REMEMBERED_EMAIL_KEY, email);
  } else {
    safeRemove(localStorage, REMEMBER_KEY);
    safeRemove(localStorage, REMEMBERED_EMAIL_KEY);
  }
}

function authStore() {
  return isRememberMe() ? localStorage : sessionStorage;
}

function otherStore() {
  return isRememberMe() ? sessionStorage : localStorage;
}

export function getAccountType() {
  return safeGet(sessionStorage, "account_type") || safeGet(localStorage, "account_type");
}

export function isClientPortalSession() {
  return getAccountType() === "client";
}

export function getAuthItem(key) {
  const fromSession = safeGet(sessionStorage, key);
  if (fromSession != null) return fromSession;
  return safeGet(localStorage, key);
}

export function setAuthItem(key, value) {
  if (value == null) {
    safeRemove(sessionStorage, key);
    safeRemove(localStorage, key);
    return;
  }
  safeSet(authStore(), key, value);
  safeRemove(otherStore(), key);
}

export function removeAuthItem(key) {
  safeRemove(sessionStorage, key);
  safeRemove(localStorage, key);
}

function persistCommon({ access_token, refresh_token, scope, tenant, tenants }, accountType) {
  clearKeys(localStorage);
  clearKeys(sessionStorage);
  const store = authStore();
  if (access_token) safeSet(store, "access_token", access_token);
  if (refresh_token) safeSet(store, "refresh_token", refresh_token);
  if (scope) safeSet(store, "scope", scope);
  if (tenant) safeSet(store, "tenant", JSON.stringify(tenant));
  if (Array.isArray(tenants)) safeSet(store, "tenants", JSON.stringify(tenants));
  safeSet(store, "account_type", accountType);
  safeSet(store, "is_authenticated", "true");
}

export function persistClientPortalSession(data) {
  persistCommon(data, "client");
  if (data.client) safeSet(authStore(), "client", JSON.stringify(data.client));
}

export function persistStaffSession(data) {
  persistCommon(data, "staff");
  if (data.user) safeSet(authStore(), "user", JSON.stringify(data.user));
}

/** platform | owner | employee (null for sessions created before the unified login). */
export function getSessionScope() {
  return getAuthItem("scope");
}

function parseJson(raw, fallback) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
}

/** Business this session acts in ({ Tenant_Id, Company_Name, ... }). */
export function getSessionTenant() {
  return parseJson(getAuthItem("tenant"), null);
}

/** Businesses the signed-in person can switch between. */
export function getSessionTenants() {
  const list = parseJson(getAuthItem("tenants"), []);
  return Array.isArray(list) ? list : [];
}

/** Store a sign-in / switch-tenant response for whichever app it belongs to. */
export function persistSignedInSession(data) {
  if (data?.account_type === "client") {
    persistClientPortalSession(data);
    return;
  }
  persistStaffSession(data);
  const perms = data?.user?.roles?.[0]?.permissions;
  if (Array.isArray(perms) && perms.length > 0) {
    safeSet(authStore(), "user_role_permissions", JSON.stringify(perms));
  }
}

export function updateClientPortalProfile(client) {
  if (!client) return;
  safeSet(authStore(), "client", JSON.stringify(client));
  safeRemove(otherStore(), "client");
}

export function clearAuthStorageKeys() {
  clearKeys(localStorage);
  clearKeys(sessionStorage);
}

/**
 * Move any leftover auth keys from localStorage → sessionStorage once,
 * then clear localStorage auth so a later tab close cannot resurrect the session.
 * Remembered sessions ("Remember me") stay in localStorage.
 */
export function migrateLegacyAuthToSession() {
  try {
    if (isRememberMe()) {
      clearKeys(sessionStorage);
      // Sessions from before the unified login carry tokens the API now rejects.
      if (!safeGet(localStorage, "scope")) clearKeys(localStorage);
      return;
    }

    const sessionType = safeGet(sessionStorage, "account_type");
    if (sessionType === "client" || sessionType === "staff") {
      clearKeys(localStorage);
      // Sessions from before the unified login carry tokens the API now rejects.
      if (!safeGet(sessionStorage, "scope")) clearKeys(sessionStorage);
      return;
    }

    const localType = safeGet(localStorage, "account_type");
    if (localType !== "client" && localType !== "staff") {
      // Orphan token cleanup
      if (safeGet(localStorage, "access_token") || safeGet(localStorage, "refresh_token")) {
        clearKeys(localStorage);
      }
      return;
    }

    if (safeGet(localStorage, "scope")) {
      AUTH_KEYS.forEach((key) => {
        const value = safeGet(localStorage, key);
        if (value != null) safeSet(sessionStorage, key, value);
      });
    }
    clearKeys(localStorage);
  } catch (_) {}
}

/** @deprecated Use migrateLegacyAuthToSession */
export function migrateLegacyClientAuthToSession() {
  migrateLegacyAuthToSession();
}
